/**
 * useCampaignListPreview
 *
 * Hook server-side para preview seguro de uma lista de contatos (campaign_lists).
 * Substitui o SELECT direto na tabela campaign_list_items, garantindo:
 *   - Validação de tenant (RPC SECURITY DEFINER)
 *   - Mascaramento de telefone e e-mail no banco
 *   - Limite fixo de 50 registros
 *
 * RELEASE:
 *   2026-05-17 v1.0.0 - Versão inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useCampaignListPreview');

export interface CampaignListPreviewItem {
  id: string;
  sort_order: number;
  name: string | null;
  phone_masked: string | null;
  email_masked: string | null;
  variables: Record<string, string>;
  status: string;
}

/** Busca até 50 itens mascarados de uma lista. */
export function useCampaignListPreview(listId: string | null, enabled: boolean) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['campaign-list-preview', currentTenant?.id, listId],
    enabled: enabled && !!listId && !!currentTenant?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<CampaignListPreviewItem[]> => {
      const { data, error } = await (supabase.rpc as any)('get_campaign_list_preview', {
        p_list_id: listId,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) {
        logger.error('Erro ao carregar preview de lista:', error);
        throw error;
      }

      return ((data ?? []) as any[]).map((row) => ({
        id: row.id,
        sort_order: row.sort_order,
        name: row.name,
        phone_masked: row.phone_masked,
        email_masked: row.email_masked,
        variables:
          row.variables && typeof row.variables === 'object'
            ? (row.variables as Record<string, string>)
            : {},
        status: row.status,
      }));
    },
  });
}
